import * as THREE from 'three';
import logger from '../util/logger.js';

// DepthPass: manage a drawing-buffer sized depth render target and provide
// a small API for rendering scene depth and retrieving the depthTexture.
export function createDepthPass(renderer) {
	let depthTarget = null;
	let lastW = 0, lastH = 0;
	// feature-detect depth texture support
	let _supported = false;
	try {
		const gl = renderer.getContext && renderer.getContext();
		const isWebGL2 = renderer.capabilities && !!renderer.capabilities.isWebGL2;
		const ext = gl && (gl.getExtension('WEBGL_depth_texture') || gl.getExtension('WEBKIT_WEBGL_depth_texture'));
		_supported = !!(isWebGL2 || ext);
		if (!_supported) logger.info('DepthPass: depth texture not supported; occlusion disabled/fallback');
	} catch (e) {
		_supported = false;
		logger.warn('DepthPass: feature detection failed', e);
	}

	function ensureSize(cssW, cssH) {
		const pr = renderer.getPixelRatio ? renderer.getPixelRatio() : (window.devicePixelRatio || 1);
		const rw = Math.max(1, Math.floor(cssW * pr));
		const rh = Math.max(1, Math.floor(cssH * pr));
		if (!_supported) return null;
		if (depthTarget && depthTarget.width === rw && depthTarget.height === rh) return depthTarget;
		if (depthTarget) depthTarget.dispose();
		const depthTex = new THREE.DepthTexture();
		depthTex.type = (THREE.UnsignedIntType && _supported) ? THREE.UnsignedIntType : THREE.UnsignedShortType;
		depthTex.format = THREE.DepthFormat;
		depthTarget = new THREE.WebGLRenderTarget(rw, rh, { depthTexture: depthTex, depthBuffer: true });
		lastW = rw; lastH = rh;
		return depthTarget;
	}

	// Render the scene depth into the RT. `options.hideObjects` can be an array
	// of objects to temporarily hide while rendering depth (e.g. sensors).
	function renderDepth(scene, camera, options = {}) {
		const cssW = options.cssWidth || renderer.domElement.clientWidth || window.innerWidth;
		const cssH = options.cssHeight || renderer.domElement.clientHeight || window.innerHeight;
		const rt = ensureSize(cssW, cssH);
		if (!rt) return null;
		const pr = renderer.getPixelRatio ? renderer.getPixelRatio() : (window.devicePixelRatio || 1);
		const dw = Math.floor(cssW * pr), dh = Math.floor(cssH * pr);

		const hide = options.hideObjects || [];
		const prev = hide.map(o => ({ o, vis: o.visible }));
		hide.forEach(o => { if (o) o.visible = false; });

		const prevRT = renderer.getRenderTarget();
		renderer.setRenderTarget(rt);
		renderer.render(scene, camera);
		renderer.setRenderTarget(prevRT);

		// restore visibility
		prev.forEach(p => { if (p.o) p.o.visible = p.vis; });

		return rt.depthTexture;
	}

	function getSize() { return { width: lastW, height: lastH, target: depthTarget }; }

	function dispose() { if (depthTarget) depthTarget.dispose(); depthTarget = null; }

	return { ensureSize, renderDepth, getSize, dispose };
}

export default createDepthPass;
